import type { ComponentSize } from '@zoryn/constants'
import { isGridSpecialColumn, refreshVisibleColumns, resolveColumnWidth } from './meta'
import type { GridColumnInfo } from '../types'

export interface HeaderWidthSyncGridLike {
  bodyRef?: { value?: HTMLElement | null }
  internalData: {
    allColumns: GridColumnInfo[]
    visibleColumns: GridColumnInfo[]
    titleList: GridColumnInfo[]
  }
}

function readPaddingX(el: HTMLElement): number {
  if (typeof getComputedStyle !== 'function') {
    return 0
  }
  const style = getComputedStyle(el)
  return (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0)
}

/** 表头标题（含图标）相对单元格内容区的溢出宽度 */
export function resolveHeaderOverflowDeficit(cell: HTMLElement): number {
  const titleWrap = cell.querySelector<HTMLElement>('.z-grid__header-title')
  const titleEl = cell.querySelector<HTMLElement>('.z-grid__header-title-text')
  if (!titleEl && !titleWrap) {
    return 0
  }

  let deficit = 0
  if (titleEl && titleEl.scrollWidth > titleEl.clientWidth + 1) {
    deficit = Math.max(deficit, titleEl.scrollWidth - titleEl.clientWidth)
  }

  const measureEl = titleWrap ?? titleEl
  if (!measureEl) {
    return deficit
  }

  const contentWidth = cell.clientWidth - readPaddingX(cell)
  if (contentWidth > 0 && measureEl.scrollWidth > contentWidth + 1) {
    deficit = Math.max(deficit, measureEl.scrollWidth - contentWidth)
  }

  return deficit
}

/** 渲染后按表头标题真实占位抬升列宽，修正估算误差与尺寸切换后的偏差 */
export function syncHeaderWidthsFromDom($grid: HeaderWidthSyncGridLike): boolean {
  const root = $grid.bodyRef?.value
  if (!root || typeof document === 'undefined') {
    return false
  }

  const idToColumn = new Map($grid.internalData.allColumns.map((col) => [col.id, col]))
  let changed = false

  const cells = root.querySelectorAll<HTMLElement>(
    '.z-grid__header-cell[data-column-id]:not(.fixed--hidden)'
  )

  for (const cell of cells) {
    const columnId = cell.dataset.columnId
    if (!columnId) {
      continue
    }
    const column = idToColumn.get(columnId)
    if (!column || isGridSpecialColumn(column)) {
      continue
    }

    const deficit = resolveHeaderOverflowDeficit(cell)
    if (deficit <= 0) {
      continue
    }

    const currentWidth = resolveColumnWidth(column)
    const newWidth = currentWidth + deficit + 4
    if (newWidth <= currentWidth) {
      continue
    }

    column.width = newWidth
    column.headerMinWidth = Math.max(Number(column.headerMinWidth) || 0, newWidth)
    column.minWidth = Math.max(Number(column.minWidth) || 80, newWidth)
    changed = true
  }

  if (changed) {
    refreshVisibleColumns($grid as Parameters<typeof refreshVisibleColumns>[0])
  }

  return changed
}

export type { ComponentSize }
